import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import api, { formatINR, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Spinner, Card, Table, GhostButton, PrimaryButton } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import { BadgeCheck, Copy, CalendarCheck, Wallet, Award, ShieldCheck, Settings, ExternalLink, Camera, HeartPulse, Save, FileImage } from "lucide-react";
import { toast } from "sonner";

// Resize an image file to a small JPEG data URL (keeps Mongo docs light).
function fileToDataUrl(file, max = 512) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = () => { img.src = reader.result; };
    reader.onerror = reject;
    img.onload = () => {
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL("image/jpeg", 0.82));
    };
    img.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function Profile() {
  const { user, refresh } = useAuth();
  const [ov, setOv] = useState(null);
  const [payments, setPayments] = useState([]);
  const [certs, setCerts] = useState([]);
  const [photo, setPhoto] = useState(user?.photo_url || "");
  const [med, setMed] = useState(null);
  const [savingMed, setSavingMed] = useState(false);
  const photoInput = useRef(null);
  const docInput = useRef(null);

  useEffect(() => {
    api.get("/me/overview").then((r) => setOv(r.data)).catch(() => setOv({}));
    api.get("/me/payments").then((r) => setPayments(r.data)).catch(() => {});
    api.get("/me/certificates").then((r) => setCerts(r.data)).catch(() => {});
    api.get("/me/medical").then((r) => setMed(r.data || {})).catch(() => setMed({}));
  }, []);
  useEffect(() => { setPhoto(user?.photo_url || ""); }, [user]);
  if (!ov) return <Spinner />;

  const sporaId = ov.spora_id || "-";
  const verifyUrl = `${window.location.origin}/verify/${encodeURIComponent(sporaId)}`;

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const url = await fileToDataUrl(file, 512);
      await api.post("/me/photo", { photo_url: url });
      setPhoto(url);
      if (refresh) await refresh();
      toast.success("Photo updated");
    } catch (err) { toast.error(apiError(err) || "Could not upload photo"); }
  };

  const onMedDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try { const url = await fileToDataUrl(file, 900); setMed((m) => ({ ...m, doc_url: url })); toast.success("Document attached - remember to save"); }
    catch { toast.error("Could not read file"); }
  };

  const saveMedical = async () => {
    setSavingMed(true);
    try { const { data } = await api.post("/me/medical", med); setMed(data); toast.success("Medical record saved"); }
    catch (e) { toast.error(apiError(e)); } finally { setSavingMed(false); }
  };

  const setM = (k, v) => setMed((m) => ({ ...m, [k]: v }));

  return (
    <div className="animate-fadeUp">
      <PageHeader title="Profile" subtitle="Your SPORA ID, activity & settings" />

      <div className="grid gap-4 lg:grid-cols-3">
        {/* Identity + SPORA ID */}
        <Card className="lg:col-span-2">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-center">
            <div className="relative h-20 w-20 shrink-0">
              {photo
                ? <img src={photo} alt={user?.name} data-testid="profile-photo" className="h-20 w-20 rounded-2xl object-cover" />
                : <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-primary/15 font-display text-3xl font-extrabold text-primary">{(user?.name || "?")[0].toUpperCase()}</div>}
              <button data-testid="upload-photo-btn" onClick={() => photoInput.current?.click()}
                className="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md ring-2 ring-background hover:bg-[#FF8533]" title="Change photo">
                <Camera className="h-3.5 w-3.5" />
              </button>
              <input ref={photoInput} type="file" accept="image/*" className="hidden" data-testid="photo-file-input" onChange={onPhoto} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h2 className="font-display text-2xl font-bold">{user?.name}</h2>
                <BadgeCheck className="h-5 w-5 text-cyan" />
              </div>
              <p className="text-sm text-muted-foreground">{user?.email}</p>
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <StatusBadge status="verified" testId="profile-verified-badge" />
                {(user?.roles || []).map((r) => <span key={r} data-testid={`role-chip-${r}`} className="rounded-full bg-secondary px-2.5 py-0.5 text-xs font-medium capitalize text-secondary-foreground">{r.replace("_", " ")}</span>)}
              </div>
            </div>
          </div>
          <div className="mt-6 flex items-center justify-between rounded-xl border border-border bg-background/40 p-4">
            <div>
              <p className="font-mono text-xs uppercase text-muted-foreground">SPORA ID</p>
              <p data-testid="spora-id" className="font-mono text-lg font-bold text-primary">{sporaId}</p>
            </div>
            <GhostButton data-testid="copy-spora-id" className="py-2" onClick={() => { navigator.clipboard?.writeText(sporaId); toast.success("SPORA ID copied"); }}>
              <Copy className="h-4 w-4" /> Copy
            </GhostButton>
          </div>
        </Card>

        {/* QR verification */}
        <Card className="flex flex-col items-center justify-center text-center">
          <div className="mb-1 flex items-center gap-2 text-sm text-muted-foreground"><ShieldCheck className="h-4 w-4 text-emerald-400" /> Verify identity</div>
          <div className="my-3 rounded-xl bg-white p-3" data-testid="spora-qr">
            <QRCodeSVG value={verifyUrl} size={140} bgColor="#ffffff" fgColor="#0B0E17" level="M" />
          </div>
          <a href={verifyUrl} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-primary hover:underline">
            Open verification page <ExternalLink className="h-3 w-3" />
          </a>
          <p className="mt-2 text-xs text-muted-foreground">Scan to confirm this is a genuine SPORA1 member. Only name, photo & role are shown publicly.</p>
        </Card>
      </div>

      {/* Medical record (private to the member) */}
      <Card className="mt-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2"><HeartPulse className="h-5 w-5 text-rose-500" /><h3 className="font-display text-lg font-semibold">Medical record</h3></div>
          <span className="rounded-full bg-secondary px-2.5 py-0.5 text-xs text-muted-foreground">Private - only you can see this</span>
        </div>
        {med === null ? <div className="py-6"><Spinner /></div> : (
          <>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <Field label="Blood group" testId="med-blood" value={med.blood_group} onChange={(v) => setM("blood_group", v)} placeholder="e.g. O+" />
              <Field label="Allergies" testId="med-allergies" value={med.allergies} onChange={(v) => setM("allergies", v)} placeholder="e.g. Penicillin, peanuts" />
              <Field label="Medical conditions" testId="med-conditions" value={med.conditions} onChange={(v) => setM("conditions", v)} placeholder="e.g. Asthma" />
              <Field label="Current medications" testId="med-medications" value={med.medications} onChange={(v) => setM("medications", v)} placeholder="e.g. Inhaler" />
              <Field label="Emergency contact name" testId="med-ename" value={med.emergency_name} onChange={(v) => setM("emergency_name", v)} placeholder="Parent / guardian" />
              <Field label="Emergency contact phone" testId="med-ephone" value={med.emergency_phone} onChange={(v) => setM("emergency_phone", v)} placeholder="+91 ..." />
            </div>
            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="flex items-center gap-3">
                {med.doc_url && <img src={med.doc_url} alt="Medical document" data-testid="med-doc-preview" className="h-16 w-16 rounded-lg border border-border object-cover" />}
                <GhostButton data-testid="upload-med-doc-btn" onClick={() => docInput.current?.click()}><FileImage className="h-4 w-4" /> {med.doc_url ? "Replace document" : "Attach document / report"}</GhostButton>
                <input ref={docInput} type="file" accept="image/*" className="hidden" data-testid="med-doc-input" onChange={onMedDoc} />
              </div>
              <PrimaryButton data-testid="save-medical-btn" className="sm:ml-auto" onClick={saveMedical} disabled={savingMed}>
                <Save className="h-4 w-4" /> Save medical record
              </PrimaryButton>
            </div>
          </>
        )}
      </Card>

      {/* Stats */}
      <div className="mt-4 grid gap-4 sm:grid-cols-4">
        <Mini icon={CalendarCheck} label="Bookings" value={ov.bookings} />
        <Mini icon={Award} label="Certificates" value={ov.certificates} />
        <Mini icon={Wallet} label="Total paid" value={formatINR(ov.total_paid)} />
        <Mini icon={ShieldCheck} label="Participants" value={ov.participants} />
      </div>

      {/* Payments history */}
      <h3 className="mb-3 mt-6 font-display text-xl font-semibold">Payment history</h3>
      {payments.length === 0 ? (
        <Card className="text-center text-sm text-muted-foreground">No payments yet.</Card>
      ) : (
        <Table head={["Booking", "Amount", "Mode", "Status", "Date"]}>
          {payments.map((p) => (
            <tr key={p.id} data-testid={`profile-payment-${p.id}`} className="border-b border-border/60 last:border-0 hover:bg-background/40">
              <td className="px-4 py-3 font-mono text-xs text-primary">{p.booking_reference}</td>
              <td className="px-4 py-3 font-mono">{formatINR(p.amount)}</td>
              <td className="px-4 py-3 text-xs text-muted-foreground">{p.simulated ? "Test" : "Razorpay"}</td>
              <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
              <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{(p.paid_at || p.created_at || "").slice(0, 10)}</td>
            </tr>
          ))}
        </Table>
      )}

      {(user?.roles || []).some((r) => ["school_coordinator", "ops", "admin"].includes(r)) && (
        <Link to="/app/school-program" data-testid="school-program-link"
          className="mt-4 flex items-center justify-between rounded-2xl border border-border bg-card p-5 transition-all hover:border-primary/50 hover:shadow-lg">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary"><ShieldCheck className="h-5 w-5" /></span>
            <div>
              <p className="font-display text-base font-semibold">School Sports Program</p>
              <p className="text-xs text-muted-foreground">Enrol, upload student roster & issue SPORA IDs</p>
            </div>
          </div>
          <ExternalLink className="h-4 w-4 text-muted-foreground" />
        </Link>
      )}

      {/* Settings (minimal) */}
      <Card className="mt-6">
        <div className="flex items-center gap-2"><Settings className="h-5 w-5 text-primary" /><h3 className="font-display text-lg font-semibold">Settings</h3></div>
        <div className="mt-3 grid gap-3 sm:grid-cols-2 text-sm">
          <Row label="Name" value={user?.name} />
          <Row label="Email" value={user?.email} />
          <Row label="Phone" value={user?.phone || "-"} />
          <Row label="Roles" value={(user?.roles || []).join(", ")} />
        </div>
      </Card>
    </div>
  );
}

const Mini = ({ icon: Icon, label, value }) => (
  <Card><div className="mb-1 flex items-center gap-2"><Icon className="h-4 w-4 text-primary" /><p className="font-mono text-xs uppercase text-muted-foreground">{label}</p></div><p className="font-display text-2xl font-bold">{value}</p></Card>
);
const Field = ({ label, value, onChange, placeholder, testId }) => (
  <div>
    <label className="mb-1 block text-xs font-medium text-muted-foreground">{label}</label>
    <input data-testid={testId} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
  </div>
);
const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4 rounded-lg border border-border bg-background/40 px-3 py-2"><span className="text-muted-foreground">{label}</span><span className="font-medium capitalize">{value}</span></div>
);
